import { type DeepPartial } from "react-hook-form";

import { DSApi } from "@ds/services/ds-api";
import { parseQueries } from "@ds/utils/query";
import { type ApiService, type ApiServiceConfig } from "@ds/services/types";

interface CampaignItemsServiceConfig extends ApiServiceConfig {
  api: DSApi;
}

class CampaignItemsService implements ApiService {
  private config: CampaignItemsServiceConfig = {
    api: {} as DSApi,
  };

  configure(cfg: CampaignItemsServiceConfig) {
    this.config = { ...cfg };
  }

  async getCampaignItems(queryInfo: QueryInfo, pagination: Pagination) {
    const queries = parseQueries(queryInfo, pagination);
    return this.config.api.get<QueryOutput<ApiCampaignItem>>("/campaign-items/", queries);
  }

  async getCampaignItem(id: number) {
    return this.config.api.get<ApiCampaignItem>(`/campaign-items/${id}`);
  }

  async createCampaignItem(item: CampaignItem) {
    return this.config.api.post<ApiCampaignItem>("/campaign-items/", item);
  }

  async updateCampaignItems(ids: number[], data: DeepPartial<CampaignItem>) {
    const payload = data.settings !== undefined ? { ...data, settings: JSON.stringify(data.settings) } : data;
    return this.config.api.patch<ApiCampaignItem>("/campaign-items/", {
      ids,
      data: payload as DeepPartial<CampaignItem>,
    });
  }

  async deleteCampaignItem(id: number) {
    return this.config.api.delete<ApiCampaignItem>(`/campaign-items/${id}`);
  }

  async deleteCampaignItems(ids: number[]) {
    return this.config.api.delete<ApiCampaignItem>("/campaign-items/", { ids });
  }

  // NOTE: PUT (not PATCH) — intentional divergence from playlist-asset reorder which uses PATCH /playlist-items/
  async reorderCampaignItems(campaignId: number, orderedIds: number[]) {
    return this.config.api.put("/campaign-items/reorder", { campaign_id: campaignId, ordered_ids: orderedIds });
  }
}

export const campaignItemsService = new CampaignItemsService();
